import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Heart, Leaf, PersonSimpleTaiChi, Barbell, Clock, Tag, MapPin, Sparkle } from "@phosphor-icons/react";
import { supabase } from "../lib/supabase";
import ActivityCard from "../components/ActivityCard";

const fadeUp = {
  initial: { opacity: 0, y: 32 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] },
};

const Hero = () => {
  const { scrollY } = useScroll();
  const yBg = useTransform(scrollY, [0, 600], [0, 160]);
  const opacityBg = useTransform(scrollY, [0, 500], [1, 0.5]);
  return (
    <section className="relative min-h-[92vh] flex items-center overflow-hidden grain" data-testid="hero-section">
      <motion.div style={{ y: yBg, opacity: opacityBg }} className="absolute inset-0 z-0">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_75%_30%,rgba(143,164,137,0.22),transparent_55%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_15%_75%,rgba(229,196,181,0.18),transparent_55%)]" />
        <div className="absolute right-0 top-0 w-[55%] h-full">
          <img src="https://images.pexels.com/photos/8436686/pexels-photo-8436686.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1400" alt="" className="w-full h-full object-cover opacity-50"
            style={{ maskImage: "linear-gradient(to left, black 25%, transparent 95%)", WebkitMaskImage: "linear-gradient(to left, black 25%, transparent 95%)" }} />
        </div>
      </motion.div>
      <div className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16 py-24 w-full">
        <div className="max-w-3xl space-y-8">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-sage/40 bg-white">
            <Sparkle size={14} weight="fill" className="text-sage-deep" />
            <span className="text-[11px] uppercase tracking-[0.25em] font-medium text-sage-deep">Última hora · hasta -60%</span>
          </motion.div>
          <motion.h1 initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.1 }}
            className="font-heading text-5xl sm:text-6xl lg:text-7xl font-light tracking-tight leading-[1.02] text-forest">
            Encuentra tu espacio.<br />Reserva tu hora.<br />
            <span className="serif-italic font-medium text-sage-deep">Cuida de ti.</span>
          </motion.h1>
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.2 }}
            className="text-lg md:text-xl text-stone max-w-xl leading-relaxed">
            Yoga, pilates, fisioterapia y mucho más, cerca de ti y a precio reducido. Plazas de última hora con descuentos reales.
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.3 }}
            className="flex flex-col sm:flex-row gap-3 pt-2">
            <Link to="/actividades" data-testid="hero-cta-explore"
              className="group inline-flex items-center justify-center gap-2 bg-sage-deep text-paper px-7 py-4 font-medium tracking-wide text-sm hover:bg-forest transition rounded-full">
              Explorar ofertas <ArrowRight size={18} className="group-hover:translate-x-1 transition" />
            </Link>
            <Link to="/registro" data-testid="hero-cta-register"
              className="inline-flex items-center justify-center gap-2 border border-forest/20 text-forest px-7 py-4 font-medium tracking-wide text-sm hover:bg-white transition rounded-full">
              Crear cuenta gratis
            </Link>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

const CategoriesBlock = () => {
  const cats = [
    { type: "yoga", label: "Yoga", icon: <PersonSimpleTaiChi size={28} weight="duotone" />, img: "https://images.pexels.com/photos/8436686/pexels-photo-8436686.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" },
    { type: "pilates", label: "Pilates", icon: <Leaf size={28} weight="duotone" />, img: "https://images.pexels.com/photos/35248387/pexels-photo-35248387.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" },
    { type: "fisioterapia", label: "Fisioterapia", icon: <Heart size={28} weight="duotone" />, img: "https://images.unsplash.com/photo-1649589352101-0f77c460296f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzB8MHwxfHNlYXJjaHwyfHxzcG9ydCUyMHBvcnRyYWl0fGVufDB8fHx8MTc3ODI0MDA0NHww&ixlib=rb-4.1.0&q=85" },
    { type: "gym", label: "Gym", icon: <Barbell size={28} weight="duotone" />, img: "https://images.pexels.com/photos/31849599/pexels-photo-31849599.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" },
  ];
  return (
    <section className="relative py-24 md:py-32 px-6 md:px-12 lg:px-16">
      <div className="max-w-[1400px] mx-auto">
        <motion.div {...fadeUp} className="flex items-end justify-between flex-wrap gap-4 mb-12">
          <div>
            <span className="text-[11px] uppercase tracking-[0.3em] text-sage-deep mb-3 block">/ 01 categorías</span>
            <h2 className="font-heading text-4xl md:text-5xl font-light leading-tight max-w-2xl text-forest">
              Elige tu <span className="serif-italic font-medium text-sage-deep">disciplina</span> y respira.
            </h2>
          </div>
          <Link to="/actividades" className="text-sm uppercase tracking-[0.2em] text-stone hover:text-sage-deep transition flex items-center gap-2">
            Ver todas <ArrowRight size={14} />
          </Link>
        </motion.div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {cats.map((c, idx) => (
            <motion.div key={c.type} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.7, delay: idx * 0.08 }}>
              <Link to={`/actividades?category=${c.type}`} data-testid={`category-${c.type}`}
                className="group relative block aspect-[4/5] overflow-hidden rounded-2xl border border-forest/10 hover:border-sage transition">
                <img src={c.img} alt={c.label} className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                <div className="absolute inset-0 bg-gradient-to-t from-forest/85 via-forest/30 to-transparent" />
                <div className="absolute inset-0 p-6 flex flex-col justify-between">
                  <div className="text-paper">{c.icon}</div>
                  <div className="space-y-1">
                    <h3 className="font-heading text-2xl md:text-3xl font-medium text-paper">{c.label}</h3>
                    <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-paper/70 group-hover:text-paper transition">
                      Ver ofertas <ArrowRight size={12} />
                    </div>
                  </div>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

const HowItWorks = () => {
  const steps = [
    { n: "01", icon: <MapPin size={24} weight="duotone" />, t: "Encuentra cerca", d: "Filtra por tipo, ciudad y horario. Solo plazas reales disponibles." },
    { n: "02", icon: <Tag size={24} weight="duotone" />, t: "Aprovecha el descuento", d: "Ofertas de última hora con hasta un 60% sobre el precio normal." },
    { n: "03", icon: <Clock size={24} weight="duotone" />, t: "Reserva en segundos", d: "Sin login obligatorio. Paga, recibe tu confirmación, respira." },
  ];
  return (
    <section className="relative py-24 md:py-32 px-6 md:px-12 lg:px-16 bg-mist border-y border-forest/8">
      <div className="max-w-[1400px] mx-auto">
        <motion.div {...fadeUp} className="max-w-2xl mb-16">
          <span className="text-[11px] uppercase tracking-[0.3em] text-sage-deep mb-3 block">/ 02 cómo funciona</span>
          <h2 className="font-heading text-4xl md:text-5xl font-light leading-tight text-forest">
            Tres pasos. <span className="serif-italic font-medium text-sage-deep">Cero fricción</span>.
          </h2>
        </motion.div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {steps.map((s, idx) => (
            <motion.div key={s.n} initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.7, delay: idx * 0.12 }}
              className="bg-white rounded-2xl p-10 border border-forest/8 group hover:border-sage/40 transition">
              <div className="flex items-start justify-between mb-8">
                <span className="font-mono text-sm text-sage-deep">{s.n}</span>
                <div className="text-sage-deep">{s.icon}</div>
              </div>
              <h3 className="font-heading text-2xl font-semibold mb-3 text-forest">{s.t}</h3>
              <p className="text-stone leading-relaxed text-sm">{s.d}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

const FeaturedActivities = () => {
  const [items, setItems] = useState([]);
  useEffect(() => {
    (async () => {
      const today = new Date().toISOString().slice(0, 10);
      const { data } = await supabase
        .from("offers")
        .select("*, business:businesses(*)")
        .eq("status", "active")
        .gte("date", today)
        .order("date", { ascending: true })
        .order("start_time", { ascending: true })
        .limit(8);
      setItems(data || []);
    })();
  }, []);

  return (
    <section className="py-24 md:py-32 px-6 md:px-12 lg:px-16">
      <div className="max-w-[1400px] mx-auto">
        <motion.div {...fadeUp} className="flex items-end justify-between flex-wrap gap-4 mb-12">
          <div>
            <span className="text-[11px] uppercase tracking-[0.3em] text-sage-deep mb-3 block">/ 03 destacadas</span>
            <h2 className="font-heading text-4xl md:text-5xl font-light leading-tight max-w-2xl text-forest">
              Plazas <span className="serif-italic font-medium text-sage-deep">desbloqueándose</span> ahora.
            </h2>
          </div>
          <Link to="/actividades" className="text-sm uppercase tracking-[0.2em] text-stone hover:text-sage-deep transition flex items-center gap-2">
            Ver todas <ArrowRight size={14} />
          </Link>
        </motion.div>
        {items.length === 0 ? (
          <div className="text-center py-12 border border-dashed border-forest/15 rounded-2xl">
            <p className="text-stone">Aún no hay ofertas activas. Crea businesses + offers desde Supabase para empezar.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {items.map((a, i) => <ActivityCard key={a.id} offer={a} index={i} />)}
          </div>
        )}
      </div>
    </section>
  );
};

const CTASection = () => (
  <section className="py-32 md:py-48 px-6 md:px-12 lg:px-16 relative overflow-hidden bg-mist">
    <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(143,164,137,0.18),transparent_60%)]" />
    <div className="max-w-[1400px] mx-auto relative">
      <motion.div {...fadeUp} className="max-w-3xl">
        <h2 className="font-heading text-5xl md:text-6xl lg:text-7xl font-light leading-[1.02] text-forest">
          Tu próxima sesión te espera <span className="serif-italic font-medium text-sage-deep">ahí fuera</span>.
        </h2>
        <p className="text-stone text-lg mt-6 max-w-xl leading-relaxed">
          Mientras lees esto, otra plaza acaba de bajar de precio. No dejes que se la lleve otro.
        </p>
        <div className="flex flex-col sm:flex-row gap-3 mt-10">
          <Link to="/actividades" className="bg-sage-deep text-paper px-8 py-4 font-medium tracking-wide text-sm hover:bg-forest transition rounded-full">Encontrar mi sesión</Link>
          <Link to="/registro" className="border border-forest/20 text-forest px-8 py-4 font-medium tracking-wide text-sm hover:bg-white transition rounded-full">Crear cuenta</Link>
        </div>
      </motion.div>
    </div>
  </section>
);

const Landing = () => (
  <div data-testid="landing-page">
    <Hero />
    <CategoriesBlock />
    <HowItWorks />
    <FeaturedActivities />
    <CTASection />
  </div>
);

export default Landing;
